import invariant from 'invariant';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { CurrentUser } from 'api/currentUser';
import { FileUploadTypes } from 'api/types/shared.types';
import Box from 'components/Box';
import { getUserType, trackEvent } from 'utils/analytics';
import { waitTaskCompleted } from 'utils/asyncBackendTask';
import { configValue } from 'utils/config';
import { entries, isNotEmpty, partial } from 'utils/functional';
import { getJson, postData, postJson } from 'utils/http';
import { fileIdToUrl } from 'utils/s3file';

const InputContainer = styled(Box)`
  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    &[disabled] {
      cursor: not-allowed;
    }
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const objToFormData = (o: Record<string, any>): FormData => {
  const f = new FormData();
  entries(o).forEach(([k, v]) => f.append(k, v));
  return f;
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const uploadPresignedUrl = (f: File, r: Record<string, any>) => {
  const { params, s3File } = r;
  const formData = objToFormData(params.fields);
  formData.append('file', f);
  return postData(params.url, formData).then(() =>
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 2-3 arguments, but got 1.
    postJson(`/data/s3-uploads/${s3File.id}/done`).then(({ task }) =>
      waitTaskCompleted(task).then(() => s3File.id)
    )
  );
};

const useMock = configValue('idealist', 'fileUploadMock');

const uploadToS3 = (f: File, fileType: FileUploadTypes) =>
  postJson('/data/s3-uploads', {
    fileType,
    filename: f.name,
    size: f.size,
    // bytes
    mimeType: f.type,
  }).then(partial(uploadPresignedUrl, f));

type OwnProps = {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (arg0: Record<string, any>) => ReactNode;
  downloadAttachment?: boolean;
  name: string;
  isRequired: boolean;
  qaId: string;
  fileId: string | null | undefined;
  fileType: FileUploadTypes;
  onChange: (arg0: string | null | undefined) => void;
  onError?: (arg0: Error) => void;
  accept?: string;
  'aria-label'?: string;
  disabled?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraAnalyticsData?: Record<string, any>;
};
type Props = OwnProps & {
  currentUser: CurrentUser;
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const S3Picker = ({
  children,
  name,
  downloadAttachment,
  isRequired,
  qaId,
  fileId,
  fileType,
  onChange,
  onError,
  accept,
  'aria-label': ariaLabel,
  disabled,
  extraAnalyticsData,
  currentUser,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null | undefined>(null);
  const [urlPreview, setUrlPreview] = useState(null);
  const [filename, setFilename] = useState(null);
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [mouseActive, setMouseActive] = useState(false);

  const onMouseEnter = () => {
    setMouseActive(true);
  };

  const onMouseLeave = () => {
    setMouseActive(false);
  };

  const onDragEnter = () => {
    setDragActive(true);
    setMouseActive(true);
  };

  const onDragEnd = () => {
    setDragActive(false);
    setMouseActive(false);
  };

  const onClick = () => {
    if (!useMock && inputRef.current) {
      inputRef.current.click();
    }
  };

  const onFileChange = useCallback(() => {
    const fs = inputRef.current && inputRef.current.files;

    if (isNotEmpty(fs)) {
      invariant(fs, 'fs must be defined'); // for flow

      const f = fs[0];
      setLoading(true);
      uploadToS3(f, fileType)
        .then((newFileId) => {
          // Track non-staff S3 uploads
          if (fileType !== 'CMS_UPLOAD') {
            let fileExtension;

            if (f && f.name) {
              fileExtension = f.name.split('.').pop();
            }

            trackEvent('Success: Uploaded File', {
              file_type: fileType,
              file_extension: fileExtension,
              user_type: getUserType(currentUser),
              ...extraAnalyticsData,
            });
          }

          setFilename(f.name);
          setCreated(new Date());
          setUrlPreview(URL.createObjectURL(f));
          onChange(newFileId);
        })
        .catch((e) => {
          if (onError) onError(e);
        })
        .finally(() => {
          setLoading(false);
          setDragActive(false);
          setMouseActive(false);
        });
    }
  }, [currentUser, extraAnalyticsData, fileType, onChange, onError]);
  useEffect(() => {
    if (fileId && !filename) {
      setLoading(true);
      getJson(`/data/s3-uploads/${fileId}/meta`)
        .then(({ s3File }) => {
          setFilename(s3File.filename);
          setCreated(s3File.created);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    if (fileId && !urlPreview) {
      setUrlPreview(fileIdToUrl(fileId, downloadAttachment));
    }
  }, [fileId, urlPreview, filename, downloadAttachment]);
  return (
    <InputContainer data-qa-id={qaId}>
      <input
        aria-label={ariaLabel}
        ref={inputRef}
        required={isRequired}
        name={name}
        type="file"
        onChange={onFileChange}
        disabled={disabled}
        data-qa-id={`${qaId}-hidden-input`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragEnter={onDragEnter}
        onDragEnd={onDragEnd}
        onDragLeave={onDragEnd}
        onFocus={onMouseEnter}
        onBlur={onMouseLeave}
        accept={accept}
      />
      {children({
        onClick,
        urlPreview,
        filename,
        loading,
        dragActive: !disabled && dragActive,
        mouseActive: !disabled && mouseActive,
        created,
      })}
    </InputContainer>
  );
};

const mapStateToProps = ({ currentUser }) => ({
  currentUser,
});

// @ts-expect-error ts-migrate(2558) FIXME: Expected 5 type arguments, but got 6.
export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  {}
)(S3Picker);
